import { createContext, useContext, useEffect } from "react";
import useApi from "./ApiContext";
import { useQuery } from "@tanstack/react-query";
import LoadingScreen from "@/components/shared-ui/LoadingScreen";
import type { CompanyResponse } from "@/services/api/types";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

const CompanyContext = createContext<CompanyResponse | null>(null);

export const CompanyProvider = ({ children }: React.PropsWithChildren) => {
  const api = useApi();

  const { data: company } = useQuery({ queryKey: ["get-company"], queryFn: () => api.company.get() });

  // Push the company name up to GTM for use in analytics
  useEffect(() => {
    if (window.dataLayer && company) {
      window.dataLayer.push({
        event: "getCompany",
        companyName: company.companyName,
      });
    }
  }, [company]);

  if (!company) {
    return <LoadingScreen withoutPage />;
  }

  return (
    <CompanyContext.Provider
      value={{
        ...company,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

const useCompany = () => {
  const companyState = useContext(CompanyContext);
  if (companyState === null) {
    throw new Error(
      "Called `useCompanyState` outside the scope of CompanyContext.provider"
    );
  }
  return companyState;
};

export default useCompany;
